import React, { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Instagram } from "lucide-react";
import { api, whatsappLink } from "../lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch { toast.error("Could not send. Try again."); }
    setLoading(false);
  };

  return (
    <div data-testid="contact-page" className="min-h-screen">
      <section className="relative h-[40vh] flex items-end border-b border-white/5">
        <img src="https://images.unsplash.com/photo-1590506978404-d3e577192a18?w=2000" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/55 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-10">
          <div className="overline mb-3">Get In Touch</div>
          <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tighter">Reach The Forge</h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-4 space-y-7">
          <div>
            <div className="overline mb-1.5">Email</div>
            <a href="mailto:hello@mileforge.in" className="font-display text-xl hover:text-[#D96A1B]">hello@mileforge.in</a>
          </div>
          <div>
            <div className="overline mb-1.5">Phone</div>
            <a href="tel:+919884041979" className="font-display text-xl hover:text-[#D96A1B]">+91 98840 41979</a>
          </div>
          <div className="pt-6 border-t border-white/10">
            <a
              data-testid="contact-whatsapp"
              href={whatsappLink()}
              target="_blank" rel="noreferrer"
              className="btn-ghost inline-flex w-full justify-center"
            >
              <MessageCircle size={16} /> Chat on WhatsApp
            </a>
          </div>
          <div data-testid="business-hours" className="pt-4">
            <div className="overline mb-1.5">Business Hours</div>
            <p className="text-sm font-body text-white/70 leading-relaxed">Monday – Saturday<br />10:00 AM – 7:00 PM IST</p>
          </div>
          <div data-testid="contact-registered-office" className="pt-4">
            <div className="overline mb-1.5 flex items-center gap-1.5"><MapPin size={12} /> Registered Office</div>
            <p className="text-sm font-body text-white/70 leading-relaxed">
              Touring Craft &amp; Riders Cafe<br />
              Chennai, Tamil Nadu<br />
              India
            </p>
            <p className="text-xs font-body text-white/40 mt-2">
              GSTIN: <span className="text-white/70">33AIUPC4644A1ZL</span>
            </p>
          </div>
          <div className="flex flex-col gap-3 pt-2">
            <a
              href="https://instagram.com/mileforge"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-instagram-mileforge"
              className="text-white/70 hover:text-[#D96A1B] flex items-center gap-2"
            >
              <Instagram size={18} />
              <span className="text-sm font-display tracking-widest uppercase">@mileforge</span>
            </a>
            <a
              href="https://instagram.com/touringcraft"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-instagram-touringcraft"
              className="text-white/55 hover:text-[#D96A1B] flex items-center gap-2"
            >
              <Instagram size={16} />
              <span className="text-xs font-display tracking-widest uppercase">@touringcraft <span className="text-white/35">— community</span></span>
            </a>
          </div>
        </aside>

        <form onSubmit={submit} className="lg:col-span-8 border border-white/10 p-8 lg:p-10 space-y-5" data-testid="contact-form">
          <h2 className="font-display text-3xl uppercase tracking-tight">Drop A Line.</h2>
          <p className="text-white/60 font-body">We reply within 24 hours. Promise.</p>

          <div className="grid sm:grid-cols-2 gap-5">
            <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B]" />
            <input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B]" />
          </div>
          <input data-testid="contact-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject" className="w-full bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B]" />
          <textarea data-testid="contact-message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows="6" placeholder="Tell us what's on your mind..." className="w-full bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B]" />
          <button data-testid="contact-submit" disabled={loading} className="btn-orange">{loading ? "Sending..." : "Send Message"}</button>
        </form>
      </div>
    </div>
  );
}
